import type {
  ClinicQuestionsInput,
  FlagRuleWhen,
  FollowUpWhen,
  Question,
  QuestionInput,
  QuestionOption,
  QuestionOptionInput,
  QuestionType,
  QuestionnaireSection,
  QuestionnaireVersion,
  SectionKey,
  UpdateVersionInput,
} from './api'
import { SECTION_KEYS } from './api'
import { SECTION_NAMES, answerChoices } from './questionnaire-labels'

/**
 * O que a tela guarda enquanto a pessoa edita o questionário, e como isso vira o corpo da API.
 * Os campos são texto, como estão no campo; a conversão para a API apara, troca vazio por nulo e
 * tira o que o tipo não usa. A validação adianta as mensagens de `anamneses/questionnaire.ts`:
 * a regra que vale é a da API (T28), e aqui ela aparece ao sair do campo.
 */

export const CHOICE_TYPES: ReadonlySet<QuestionType> = new Set([
  'single_choice',
  'multiple_choice',
  'scale',
])

export const isChoiceType = (type: QuestionType) => CHOICE_TYPES.has(type)

export interface OptionDraft {
  uid: string
  value: string
  label: string
  examples: string
  opensFollowUp: boolean
  isExclusive: boolean
  raisesFlag: boolean
  attentionLabel: string
}

export interface FollowUpDraft {
  when: FollowUpWhen
  label: string
  example: string
  isRequired: boolean
  maxLength: number
}

export interface FlagRuleDraft {
  when: FlagRuleWhen
  value: string
  attentionLabel: string
}

export interface QuestionDraft {
  uid: string
  key: string
  label: string
  shortLabel: string
  context: string
  type: QuestionType
  allowsUnsure: boolean
  isRequired: boolean
  isSkippable: boolean
  isSensitive: boolean
  options: OptionDraft[]
  condition: { questionKey: string; values: string[] } | null
  followUp: FollowUpDraft | null
  flagRule: FlagRuleDraft | null
  example: string
  min: string
  max: string
  review: { legal: boolean; clinical: boolean }
}

export interface SectionDraft {
  key: SectionKey
  name: string
  intro: string
  isSensitive: boolean
  questions: QuestionDraft[]
}

export interface VersionDraft {
  declarationVersion: string
  declarationText: string
  source: string
  sections: SectionDraft[]
}

export const newUid = () => globalThis.crypto.randomUUID()

export function emptyOption(): OptionDraft {
  return {
    uid: newUid(),
    value: '',
    label: '',
    examples: '',
    opensFollowUp: false,
    isExclusive: false,
    raisesFlag: false,
    attentionLabel: '',
  }
}

export function emptyQuestion(type: QuestionType = 'yes_no'): QuestionDraft {
  return {
    uid: newUid(),
    key: '',
    label: '',
    shortLabel: '',
    context: '',
    type,
    allowsUnsure: false,
    isRequired: true,
    isSkippable: false,
    isSensitive: false,
    options: isChoiceType(type) ? [emptyOption(), emptyOption()] : [],
    condition: null,
    followUp: null,
    flagRule: null,
    example: '',
    min: '',
    max: '',
    review: { legal: false, clinical: false },
  }
}

export function emptyFollowUp(when: FollowUpWhen): FollowUpDraft {
  return { when, label: '', example: '', isRequired: true, maxLength: 300 }
}

export function emptyFlagRule(when: FlagRuleWhen): FlagRuleDraft {
  return { when, value: '', attentionLabel: '' }
}

// Da API para a tela -------------------------------------------------------------------------------

export function fromOption(option: QuestionOption): OptionDraft {
  return {
    uid: option.id || newUid(),
    value: option.value,
    label: option.label,
    examples: option.examples ?? '',
    opensFollowUp: option.opensFollowUp,
    isExclusive: option.isExclusive,
    raisesFlag: option.raisesFlag,
    attentionLabel: option.attentionLabel ?? '',
  }
}

export function fromQuestion(question: Question): QuestionDraft {
  return {
    uid: question.id || newUid(),
    key: question.key,
    label: question.label,
    shortLabel: question.shortLabel,
    context: question.context ?? '',
    type: question.type,
    allowsUnsure: question.allowsUnsure,
    isRequired: question.isRequired,
    isSkippable: question.isSkippable,
    isSensitive: question.isSensitive,
    // Em sim ou não as opções são fixas e voltam na leitura; aqui só as de escolha contam.
    options: isChoiceType(question.type) ? question.options.map(fromOption) : [],
    condition: question.condition
      ? { questionKey: question.condition.questionKey, values: [...question.condition.values] }
      : null,
    followUp: question.followUp ? { ...question.followUp } : null,
    flagRule: question.flagRule
      ? {
          when: question.flagRule.when,
          value: question.flagRule.value == null ? '' : String(question.flagRule.value),
          attentionLabel: question.flagRule.attentionLabel,
        }
      : null,
    example: question.example ?? '',
    min: question.min == null ? '' : String(question.min),
    max: question.max == null ? '' : String(question.max),
    review: { ...question.review },
  }
}

export function fromSection(section: QuestionnaireSection): SectionDraft {
  return {
    key: section.key as SectionKey,
    name: section.name,
    intro: section.intro ?? '',
    isSensitive: section.isSensitive,
    questions: section.questions.map(fromQuestion),
  }
}

/** A versão como a tela a edita; sem blocos na resposta, os quatro do desenho, vazios. */
export function fromVersion(version: QuestionnaireVersion): VersionDraft {
  const sections = version.sections ?? []
  return {
    declarationVersion: version.declaration.version ?? '',
    declarationText: version.declaration.text ?? '',
    source: version.source ?? '',
    sections: SECTION_KEYS.map((key) => {
      const found = sections.find((section) => section.key === key)
      return found
        ? fromSection(found)
        : {
            key,
            name: SECTION_NAMES[key],
            intro: '',
            isSensitive: key === 'selfImage',
            questions: [],
          }
    }),
  }
}

// Da tela para a API -------------------------------------------------------------------------------

const text = (value: string) => value.trim()
const optional = (value: string) => value.trim() || null
const integer = (value: string) => (value.trim() === '' ? null : Number(value.trim()))

export function toOptionInput(option: OptionDraft): QuestionOptionInput {
  return {
    value: text(option.value),
    label: text(option.label),
    examples: optional(option.examples),
    opensFollowUp: option.opensFollowUp,
    isExclusive: option.isExclusive,
    raisesFlag: option.raisesFlag,
    attentionLabel: optional(option.attentionLabel),
  }
}

export function toQuestionInput(question: QuestionDraft): QuestionInput {
  const choice = isChoiceType(question.type)
  return {
    key: text(question.key),
    label: text(question.label),
    shortLabel: text(question.shortLabel),
    context: optional(question.context),
    type: question.type,
    allowsUnsure: question.type === 'yes_no' && question.allowsUnsure,
    isRequired: question.isRequired,
    isSkippable: question.isSkippable,
    isSensitive: question.isSensitive,
    options: choice ? question.options.map(toOptionInput) : [],
    condition: question.condition,
    followUp: question.followUp
      ? {
          ...question.followUp,
          label: text(question.followUp.label),
          example: text(question.followUp.example),
        }
      : null,
    flagRule: question.flagRule
      ? {
          when: question.flagRule.when,
          value:
            question.flagRule.when === 'below' || question.flagRule.when === 'above'
              ? integer(question.flagRule.value)
              : null,
          attentionLabel: text(question.flagRule.attentionLabel),
        }
      : null,
    example: optional(question.example),
    min: question.type === 'number' ? integer(question.min) : null,
    max: question.type === 'number' || question.type === 'text' ? integer(question.max) : null,
    review: question.review,
  }
}

export function toClinicQuestionsInput(
  questions: readonly QuestionDraft[],
  currentVersionId: string | null,
): ClinicQuestionsInput {
  return { currentVersionId, questions: questions.map(toQuestionInput) }
}

export function toVersionInput(draft: VersionDraft): UpdateVersionInput {
  return {
    declaration: { version: text(draft.declarationVersion), text: text(draft.declarationText) },
    source: optional(draft.source),
    sections: draft.sections.map((section) => ({
      key: section.key,
      name: text(section.name),
      intro: optional(section.intro),
      isSensitive: section.isSensitive,
      questions: section.questions.map(toQuestionInput),
    })),
  }
}

// Chave e valor (P-ANA-64) -------------------------------------------------------------------------

const fold = (value: string) => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

/** Até 41 caracteres: o que a API aceita em `key` (`[a-z][a-zA-Z0-9]{2,40}`). */
const KEY_MAX = 41
const VALUE_MAX = 40

function withSuffix(base: string, max: number, taken: ReadonlySet<string>, join = '') {
  if (!taken.has(base)) return base
  for (let n = 2; ; n++) {
    const suffix = `${join}${n}`
    const candidate = `${base.slice(0, max - suffix.length)}${suffix}`
    if (!taken.has(candidate)) return candidate
  }
}

/**
 * A chave de uma pergunta a partir do texto dela, em camelCase ("Qual seu estilo musical?" →
 * `qualSeuEstiloMusical`), única entre as `taken`. A gestora nunca a vê; ela nasce na primeira
 * digitação e fica, para a identidade da pergunta sobreviver a uma reescrita.
 */
export function deriveKey(label: string, taken: ReadonlySet<string> = new Set()) {
  const words = fold(label)
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
  let key = words
    .map((word, index) => (index === 0 ? word : word[0]!.toUpperCase() + word.slice(1)))
    .join('')
  if (!/^[a-z]/.test(key)) key = `q${key}`
  key = key.slice(0, KEY_MAX)
  while (key.length < 3) key = `${key}x`
  return withSuffix(key, KEY_MAX, taken)
}

/** O valor de uma opção a partir do rótulo ("Não sei dizer" → `nao_sei_dizer`), único na pergunta. */
export function deriveOptionValue(label: string, taken: ReadonlySet<string> = new Set()) {
  let value = fold(label)
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
  if (!/^[a-z]/.test(value)) value = `o${value ? `_${value}` : ''}`
  value = value.slice(0, VALUE_MAX).replace(/_+$/g, '')
  return withSuffix(value, VALUE_MAX, taken, '_')
}

// Validação (as mensagens de `anamneses/questionnaire.ts`) -------------------------------------------

export const MSG = {
  noHtml: 'Escreva só o texto, sem HTML.',
  key: 'A chave começa com letra minúscula e tem de 3 a 41 letras e números, sem espaço.',
  keyUnique: 'Esta chave já é de outra pergunta desta versão. Chave nova é pergunta nova.',
  clinicKey: 'Cada pergunta da clínica precisa de uma chave própria.',
  label: 'A pergunta precisa de 5 a 200 caracteres.',
  shortLabel: 'O rótulo curto tem de 1 a 40 caracteres.',
  context: 'O contexto pode ter no máximo 300 caracteres.',
  options: 'Uma pergunta de escolha precisa de 2 a 12 opções.',
  optionValue: 'O valor da opção tem letras minúsculas, números e _, como anticoagulant.',
  optionUnique: 'Cada opção precisa de um valor próprio.',
  optionLabel: 'O rótulo da opção tem de 1 a 80 caracteres.',
  optionExamples: 'Os exemplos da opção podem ter no máximo 300 caracteres.',
  attentionLabel: 'Escreva a linha do bloco de atenção em até 120 caracteres.',
  followUpLabel: 'Diga o que a paciente deve descrever, em até 120 caracteres.',
  followUpExample: 'Dê um exemplo real de resposta; ele aparece como dica.',
  followUpOptions: 'Marque ao menos uma opção que abre o campo de descrição.',
  flagOptions: 'Marque ao menos uma opção que pede atenção.',
  flagValue: 'Informe o número a partir do qual a resposta pede atenção.',
  example: 'O exemplo pode ter no máximo 200 caracteres.',
  range: 'O máximo precisa ser maior ou igual ao mínimo.',
  textMax: 'Uma resposta de texto pode ter de 1 a 2.000 caracteres.',
  integer: 'Informe um número inteiro.',
  conditionKey: 'A condição aponta para uma pergunta anterior do mesmo bloco.',
  conditionValues: 'A condição usa respostas que existem na pergunta indicada.',
  sectionName: 'O nome do bloco tem de 1 a 80 caracteres.',
  sectionIntro: 'A introdução do bloco pode ter no máximo 300 caracteres.',
  declarationVersion: 'Informe a versão da declaração em até 20 caracteres, como 2026-09.',
  declarationText: 'Escreva o texto da declaração em até 2.000 caracteres.',
  source: 'A origem do conteúdo pode ter no máximo 500 caracteres.',
  justification: 'Escreva o motivo em 10 a 500 caracteres.',
} as const

/** Erros por caminho dentro da pergunta: `label`, `options.2.label`, `followUp.example`… */
export type FieldErrorMap = Record<string, string>

const KEY = /^[a-z][a-zA-Z0-9]{2,40}$/
const OPTION_VALUE = /^[a-z][a-z0-9_]{0,39}$/
const hasHtml = (value: string) => /[<>]/.test(value)

function checkText(
  errors: FieldErrorMap,
  path: string,
  value: string,
  min: number,
  max: number,
  message: string,
) {
  const trimmed = value.trim()
  if (trimmed.length < min || trimmed.length > max) errors[path] = message
  else if (hasHtml(trimmed)) errors[path] = MSG.noHtml
}

const isInteger = (value: string) => /^-?\d+$/.test(value.trim())

export interface QuestionContext {
  /** Pergunta da clínica: sem condição e com a chave única entre as da clínica. */
  isClinic: boolean
  /** As perguntas anteriores do mesmo bloco, para a condição. */
  previous: readonly QuestionDraft[]
  /** As chaves das outras perguntas (do bloco, da versão ou da clínica). */
  takenKeys: ReadonlySet<string>
}

/** As regras de uma pergunta, campo a campo e cruzadas (RN-ANA-11, 18, 19 e 20). */
export function validateQuestion(q: QuestionDraft, ctx: QuestionContext): FieldErrorMap {
  const errors: FieldErrorMap = {}
  if (!KEY.test(q.key.trim())) errors.key = MSG.key
  else if (ctx.takenKeys.has(q.key.trim()))
    errors.key = ctx.isClinic ? MSG.clinicKey : MSG.keyUnique
  checkText(errors, 'label', q.label, 5, 200, MSG.label)
  checkText(errors, 'shortLabel', q.shortLabel, 1, 40, MSG.shortLabel)
  if (q.context.trim()) checkText(errors, 'context', q.context, 0, 300, MSG.context)
  if (q.example.trim()) checkText(errors, 'example', q.example, 0, 200, MSG.example)

  if (isChoiceType(q.type)) {
    if (q.options.length < 2 || q.options.length > 12) errors.options = MSG.options
    const values = q.options.map((option) => option.value.trim())
    if (new Set(values).size !== values.length) errors.options = MSG.optionUnique
    q.options.forEach((option, index) => {
      checkText(errors, `options.${index}.label`, option.label, 1, 80, MSG.optionLabel)
      if (!OPTION_VALUE.test(option.value.trim())) {
        errors[`options.${index}.value`] = MSG.optionValue
      }
      if (option.examples.trim()) {
        checkText(errors, `options.${index}.examples`, option.examples, 0, 300, MSG.optionExamples)
      }
      if (option.attentionLabel.trim()) {
        checkText(
          errors,
          `options.${index}.attentionLabel`,
          option.attentionLabel,
          0,
          120,
          MSG.attentionLabel,
        )
      }
    })
  }

  if (q.followUp) {
    checkText(errors, 'followUp.label', q.followUp.label, 1, 120, MSG.followUpLabel)
    checkText(errors, 'followUp.example', q.followUp.example, 1, 200, MSG.followUpExample)
    if (q.followUp.when === 'options' && !q.options.some((option) => option.opensFollowUp)) {
      errors['followUp.when'] = MSG.followUpOptions
    }
  }

  if (q.flagRule) {
    checkText(
      errors,
      'flagRule.attentionLabel',
      q.flagRule.attentionLabel,
      1,
      120,
      MSG.attentionLabel,
    )
    if (q.flagRule.when === 'options' && !q.options.some((option) => option.raisesFlag)) {
      errors['flagRule.when'] = MSG.flagOptions
    }
    if (
      (q.flagRule.when === 'below' || q.flagRule.when === 'above') &&
      !isInteger(q.flagRule.value)
    ) {
      errors['flagRule.value'] = MSG.flagValue
    }
  }

  if (q.type === 'number') {
    if (q.min.trim() && !isInteger(q.min)) errors.min = MSG.integer
    if (q.max.trim() && !isInteger(q.max)) errors.max = MSG.integer
    if (!errors.min && !errors.max && q.min.trim() && q.max.trim()) {
      if (Number(q.min) > Number(q.max)) errors.max = MSG.range
    }
  }
  if (q.type === 'text' && q.max.trim()) {
    if (!isInteger(q.max)) errors.max = MSG.integer
    else if (Number(q.max) < 1 || Number(q.max) > 2000) errors.max = MSG.textMax
  }

  if (q.condition && !ctx.isClinic) {
    const source = ctx.previous.find((item) => item.key === q.condition?.questionKey)
    if (!source || source.condition) errors['condition.questionKey'] = MSG.conditionKey
    else {
      const accepted = answerChoices(source).map((choice) => choice.value)
      if (
        q.condition.values.length === 0 ||
        q.condition.values.some((value) => !accepted.includes(value))
      ) {
        errors['condition.values'] = MSG.conditionValues
      }
    }
  }
  return errors
}

export function validateSection(section: SectionDraft): FieldErrorMap {
  const errors: FieldErrorMap = {}
  checkText(errors, 'name', section.name, 1, 80, MSG.sectionName)
  // Bloco sensível sem introdução não impede salvar: a API devolve o aviso e barra a publicação.
  if (section.intro.trim()) checkText(errors, 'intro', section.intro, 0, 300, MSG.sectionIntro)
  return errors
}

export function validateDeclaration(draft: VersionDraft): FieldErrorMap {
  const errors: FieldErrorMap = {}
  checkText(errors, 'declarationVersion', draft.declarationVersion, 1, 20, MSG.declarationVersion)
  checkText(errors, 'declarationText', draft.declarationText, 1, 2000, MSG.declarationText)
  if (draft.source.trim()) checkText(errors, 'source', draft.source, 0, 500, MSG.source)
  return errors
}

/** As chaves de todas as perguntas da versão, menos a da própria pergunta. */
export function takenKeysExcept(sections: readonly SectionDraft[], uid: string) {
  const keys = new Set<string>()
  for (const section of sections) {
    for (const question of section.questions) {
      if (question.uid !== uid && question.key.trim()) keys.add(question.key.trim())
    }
  }
  return keys
}

/**
 * Os erros de um 422 que pertencem a uma pergunta, pelo prefixo do caminho (`questions.1.` na
 * clínica; `sections.1.questions.3.` na versão), já sem o prefixo: `options`, `followUp.when`…
 */
export function serverErrorsAt(fields: Record<string, string>, prefix: string): FieldErrorMap {
  const errors: FieldErrorMap = {}
  for (const [path, message] of Object.entries(fields)) {
    if (path === prefix.slice(0, -1)) errors[''] = message
    else if (path.startsWith(prefix)) errors[path.slice(prefix.length)] = message
  }
  return errors
}

/** Move um item de `from` para `to`, sem mudar a lista original. */
export function moveItem<T>(items: readonly T[], from: number, to: number): T[] {
  if (to < 0 || to >= items.length || from === to) return [...items]
  const next = [...items]
  const [item] = next.splice(from, 1)
  next.splice(to, 0, item as T)
  return next
}

/** Quando o campo de descrição pode abrir, pelo tipo (o primeiro é o padrão); vazio em texto. */
export function followUpWhens(type: QuestionType): FollowUpWhen[] {
  if (type === 'yes_no') return ['yes', 'no', 'always']
  if (isChoiceType(type)) return ['options', 'always']
  if (type === 'text') return []
  return ['always']
}

/** Quando a resposta pede atenção, pelo tipo (o primeiro é o padrão); vazio onde não há regra. */
export function flagRuleWhens(type: QuestionType): FlagRuleWhen[] {
  if (type === 'yes_no') return ['yes', 'no']
  if (isChoiceType(type)) return ['options']
  if (type === 'number') return ['above', 'below']
  return []
}

/** O rascunho como `Question`, para a leitura e a linha de apoio usarem o mesmo texto da API. */
export function draftAsQuestion(
  draft: QuestionDraft,
  position: number,
  source: Question['source'] = 'default',
): Question {
  const input = toQuestionInput(draft)
  return {
    id: draft.uid,
    source,
    position,
    key: input.key,
    label: input.label,
    shortLabel: input.shortLabel,
    context: input.context ?? null,
    type: input.type,
    allowsUnsure: input.allowsUnsure ?? false,
    isRequired: input.isRequired ?? true,
    isSkippable: input.isSkippable ?? false,
    isSensitive: input.isSensitive ?? false,
    condition: input.condition ?? null,
    followUp: input.followUp ?? null,
    flagRule: input.flagRule ?? null,
    example: input.example ?? null,
    min: input.min ?? null,
    max: input.max ?? null,
    review: input.review ?? { legal: false, clinical: false },
    options: (input.options ?? []).map((option, index) => ({
      id: draft.options[index]?.uid ?? `${draft.uid}-${index}`,
      value: option.value,
      label: option.label,
      examples: option.examples ?? null,
      position: index + 1,
      opensFollowUp: option.opensFollowUp ?? false,
      isExclusive: option.isExclusive ?? false,
      raisesFlag: option.raisesFlag ?? false,
      attentionLabel: option.attentionLabel ?? null,
    })),
  }
}
